import React from "react";
import { CircleCheck, HeartHandshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "@/components/common/Link";
const commitmentPoints = [{
  title: "No Reliance on Overtime",
  description: "We are building our practice around sustainable workloads, clear priorities, and a healthier way to get good work done."
}, {
  title: "No Tax Season Crunch",
  description: "RiverBend is not built around the traditional tax-season model. Our work is broader, steadier, and focused on long-term client support."
}, {
  title: "Creative, Solutions-Based Work",
  description: "Our team works through practical problems, builds useful systems, and has room to contribute ideas that improve how work gets done."
}, {
  title: "Work That Makes a Difference",
  description: "We support people, communities, and organizations as they strengthen financial systems, reporting, planning, and decision-making."
}];
export default function ReparationProfessionnelleVsBricolage() {
  return <section data-section-id="252662" id="our-commitment-to-employees" className="bg-gradient-to-br from-white via-slate-50 to-secondary-50/40 text-foreground py-16 md:py-24 relative overflow-hidden border-b border-border/40">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full bg-secondary-100/40 blur-[120px]" />
        <div className="absolute -bottom-40 -left-24 w-[420px] h-[420px] rounded-full bg-primary-100/30 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="max-w-2xl">
            <Badge variant="secondary" className="bg-secondary-100 text-secondary-700 hover:bg-secondary-100 border border-secondary-200/70 px-4 py-1.5 text-sm font-medium mb-6">
              <HeartHandshake className="w-4 h-4 mr-2 text-secondary-600" />
              Our Commitment to Employees
            </Badge>

            <div className="space-y-5">
              <h1 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight text-primary-600">
                Meaningful work,{" "}
                <span className="text-secondary-600">practical support</span>,
                and room to grow.
              </h1>

              <p className="text-lg md:text-xl text-muted-foreground max-w-xl font-default leading-relaxed">
                RiverBend is building a different kind of accounting and
                advisory firm. We are moving away from the overtime-driven,
                tax-season model and creating a workplace where people can do
                thoughtful, useful work that supports clients, communities, and
                long-term capacity.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-9">
              {commitmentPoints.map((point, i) => <div key={i} data-index={i} className="rounded-xl border border-border/70 bg-white/80 p-5 shadow-sm">
                  <div className="flex items-start gap-3">
                    <CircleCheck className="w-5 h-5 text-secondary-600 shrink-0 mt-0.5" />

                    <div className="space-y-2">
                      <h3 className="font-default font-semibold text-base text-primary-600 leading-snug">
                        {point.title}
                      </h3>

                      <p className="text-muted-foreground text-sm font-default leading-relaxed">
                        {point.description}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-9">
              <Button asChild size="lg" className="bg-primary-600 hover:bg-primary-700 text-primary-foreground text-base px-7 shadow-sm">
                <Link to="/contact/">Contact Us About Opportunities</Link>
              </Button>

              <Button asChild size="lg" variant="outline" className="border-primary-200 text-primary-600 hover:bg-primary-50 text-base px-7">
                <Link to="/career-opportunities/">View Open Roles</Link>
              </Button>
            </div>
          </div>

          <div className="w-full relative lg:pl-4">
            <div className="absolute inset-0 bg-secondary-500/10 rounded-3xl translate-x-4 translate-y-4 -z-10" />

            <div className="rounded-3xl overflow-hidden shadow-xl border border-white/80 bg-white p-3">
              <AspectRatio ratio={4 / 3} className="rounded-2xl overflow-hidden">
                <img src="https://stable-woodcock.10web.cloud/wp-content/uploads/2026/05/ChatGPT-Image-May-16-2026-09_08_51-AM.png" alt="RiverBend team members working together in a professional setting with a northern lake and forest view" className="object-cover w-full h-full" loading="lazy" />
              </AspectRatio>
            </div>

            <div className="absolute -bottom-6 left-6 right-6 sm:left-auto sm:right-auto sm:-left-6 bg-white text-foreground p-5 rounded-2xl shadow-xl border border-border max-w-[300px]">
              <p className="font-default font-semibold text-primary-600 text-base mb-2">
                Our Approach
              </p>

              <p className="text-sm text-muted-foreground font-default leading-relaxed">
                Practical support, clear expectations, creative
                problem-solving, and meaningful work that helps people grow.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}